import React from 'react'
import styled from 'styled-components'
import { Tooltip as ReactTooltip } from 'react-tippy'
import ReactPaginate from 'react-paginate'
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import { useMediaQuery } from 'react-responsive'

import {
  palette,
  fonts,
  mediaQueries,
  setOpacity,
} from '../../styles/variables'
import { getOS, getTimeLabel, isUpdatedNow } from '../../utils/functions'
import Tooltip from '../tooltip/Tooltip'
import ReturnRightHover from '../../styles/assets/tableIcons/right_arrow_hover.svg'
import refreshIcon from '../../styles/assets/tableIcons/refresh.svg'
import checkIcon from '../../styles/assets/check_green.svg'
import { CloseButton, IconButton, OutlineButton } from '../buttons/Buttons'
import { Desktop, Mobile } from '../../utils/media'
import { Text } from '../topography'
import TWText from '../ui/text'
import { translate } from '../../utils/translation'
import { LoadingWheel } from '../loading/Loading'
import Icon from '../icon/Icon'
import { formatNumber } from '../../utils/numbers'

const TableInnerWrapper = styled.div((props) => ({
  width: '100%',
  overflow: getOS() === 'Win' ? 'visible' : props?.overflow || 'hidden',
  backgroundColor: palette.lightGray10,
  color: props.noData ? `${palette.darkGray50} !important` : 'inherit',
  '& > div': {
    color: props.noData ? `${palette.darkGray50} !important` : 'inherit',
  },
  display: 'flex',
  flexDirection: 'column',
  ...(props.styles || {}),
}))

const TableOuterWrapper = styled.div((props) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  ...(props.styles || {}),
}))

const ScrollWrapper = styled.div(({ stretch, height, minHeight }) => ({
  position: 'relative',
  overflow: 'auto',
  maxHeight: (() => {
    if (stretch) {
      return 'inherit'
    }

    return height || '500px'
  })(),
  minHeight: minHeight || height,
  width: getOS() === 'Win' && !stretch ? 'calc(100% + 17px)' : '100%',
  overflowY: getOS() === 'Win' && !stretch ? 'scroll' : 'auto',
  [mediaQueries.mobile]: {
    maxHeight: height ?? '450px',
  },
}))

const DataRow = styled.div(
  ({
    hidden,
    checked,
    blockContent,
    active,
    allowBlockSelection,
    shadow,
    bgColor,
    hoverEffect,
    alignLeft = false,
    thickerBorder = false,
  }) => ({
    display: 'flex',
    [mediaQueries.mobile]: {
      display: hidden ? 'none' : 'flex',
    },
    ':nth-child(odd)': {
      backgroundColor: (() => {
        if (checked && !blockContent) {
          return palette.blue20
        }

        return active ? palette.lightGray50 : palette.lightGray20
      })(),
      '& > div': {
        backgroundColor:
          checked && (!blockContent || allowBlockSelection)
            ? palette.blue20
            : palette.lightGray10,
      },
    },
    ':nth-child(even)': {
      backgroundColor: (() => {
        if (checked && blockContent) {
          return palette.blue20
        }

        return active ? palette.lightGray50 : palette.lightGray10
      })(),

      '& > div': {
        backgroundColor:
          checked && (!blockContent || allowBlockSelection)
            ? palette.blue20
            : palette.lightGray20,
      },
    },
    [active && 'background']: palette.lightGray50,
    '& > div':
      shadow && active
        ? {
            background: palette.lightGray50,
          }
        : {},
    ':hover': shadow
      ? {
          '& > div': {
            background: palette.lightGray50,
            '&:last-of-type': {
              background: bgColor
                ? `${bgColor} !important`
                : palette.lightGray50,
              '&.block-wrapper': {
                backgroundColor: `${palette.white} !important`,
              },
            },
            [hoverEffect && '& span.arrow']: {
              backgroundImage: `url(${ReturnRightHover})`,
            },
          },
        }
      : {},
    [blockContent && '& > div']: {
      visibility: 'hidden',
      position: 'relative',
      ':after': {
        visibility: 'visible',
        position: 'absolute',
        top: '50%',
        left: alignLeft ? '5%' : '30%',
        content: "' '",
        width: '40%',
        borderTop: thickerBorder
          ? `5px solid ${palette.lightGray70}`
          : `3px solid ${palette.lightGray70}`,
        borderRadius: thickerBorder ? '10px' : '0px',
      },
      '&:first-child': {
        ':after': {
          left: 20,
        },
      },
    },
  }),
)

const DataColumnBar = styled.div({
  position: 'absolute',
  top: '0',
  left: '0',
  height: '100%',
})

const DataColumnValue = styled.div({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  height: '100%',
})

const DataRowPrimary = styled.div((props) => ({
  display: props.hidden ? 'none' : 'flex',
  flexDirection: 'column',

  ':nth-child(odd)': {
    backgroundColor:
      props.checked && (!props.blockContent || props.allowBlockSelection)
        ? palette.blue20
        : palette.lightGray10,
    '& > div': {
      backgroundColor:
        props.checked && (!props.blockContent || props.allowBlockSelection)
          ? palette.blue20
          : palette.lightGray10,
    },
  },
  ':nth-child(even)': {
    backgroundColor:
      props.checked && (!props.blockContent || props.allowBlockSelection)
        ? palette.blue20
        : palette.lightGray20,
    '& > div': {
      backgroundColor:
        props.checked && (!props.blockContent || props.allowBlockSelection)
          ? palette.blue20
          : palette.lightGray20,
    },
  },
  [props.blockContent && '& > div > div']: {
    visibility: 'hidden',
    overflow: 'hidden',
    position: 'relative',
    height: '54px',
    ':after': {
      visibility: 'visible',
      position: 'absolute',
      top: '50%',
      left: props.alignLeft ? '5%' : '30%',
      content: "' '",
      width: '40%',
      borderTop: `3px solid ${palette.lightGray70}`,
    },
    '&:nth-child(n+3)': {
      visibility: 'visible',
      '& > div': {
        visibility: 'hidden',
        position: 'relative',
        ':after': {
          visibility: 'visible',
          position: 'absolute',
          top: '50%',
          left: props.alignLeft ? '5%' : '30%',
          content: "' '",
          width: '40%',
          borderTop: props.thickerBorder
            ? `5px solid ${palette.lightGray70}`
            : `3px solid ${palette.lightGray70}`,
          borderRadius: props.thickerBorder ? '10px' : '0px',
        },
      },
    },
  },
  '@media only screen and (max-width: 375px) and (min-width: 320px)': {
    width: '96vw',
  },
  '@media only screen and (max-width: 320px)': {
    width: '110vw',
  },
}))

const DataGroup = styled.div({})

const TableTitle = styled.h3({
  fontSize: '16px',
  fontFamily: fonts.primaryBold,
  textTransform: 'uppercase',
  marginTop: '5px',
  [mediaQueries.mobile]: {
    fontSize: '12px',
  },
})

const HeadingRow = styled.div((props) => ({
  overflow: getOS() === 'Win' ? 'hidden' : 'auto',
  minHeight: props.minHeight || 'fit-content',
  display: 'flex',
  '::-webkit-scrollbar': {
    height: '0',
  },
  '::-webkit-scrollbar-track': {
    background: 'transparent',
    height: '0',
  },
  '::-webkit-scrollbar-thumb': {
    width: '0px',
    WebkitBorderRadius: '0px',
    borderRadius: '0px',
    background: 'transparent',
  },
  '::-webkit-scrollbar-thumb:window-inactive': {
    background: 'transparent',
  },
  [mediaQueries.mobile]: {
    fontSize: '10px',
  },
  ...(props.styles || {}),
}))

const LabelWrapper = styled.div((props) => ({
  fontSize: '12px',
  fontFamily: fonts.primaryBold,
  whiteSpace: props.whiteSpace || 'pre-wrap',
  ...(props.center
    ? {
        textAlign: 'center',
      }
    : {
        display: 'flex',
        alignItems: 'center',
      }),
  [mediaQueries.mobile]: {
    fontSize: '10px',
  },
}))

const Label = ({
  title,
  whiteSpace,
  center,
  mobileTitle,
  id,
  description,
  place,
  useTWText,
}) => {
  if (!title) return <LabelWrapper />

  if (useTWText) {
    return (
      <TWText className="tw-text-sm tw-font-black" renderChildren as="span">
        <Desktop>{title}</Desktop>
        <Mobile>{mobileTitle || title}</Mobile>
        {description && (
          <Tooltip
            id={id}
            text={description}
            style={{ margin: '0 5px' }}
            place={place}
          />
        )}
      </TWText>
    )
  }

  return (
    <LabelWrapper whiteSpace={whiteSpace} center={center || false}>
      <Desktop>{title}</Desktop>
      <Mobile>{mobileTitle || title}</Mobile>
      {description && (
        <Tooltip
          id={id}
          text={description}
          style={{ margin: '0 5px' }}
          place={place}
        />
      )}
    </LabelWrapper>
  )
}

const HeadingRegionalColumn = styled.div({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  borderBottom: '1px solid white',
  backgroundColor: palette.lightGray20,
  padding: '5px 16px',
  minHeight: '50px',
  fontSize: '14px',
  color: palette.darkGray,
  fontFamily: 'geomanistbold',
  textTransform: 'uppercase',
})

const HeadingColumnCheckbox = styled.div((props) => ({
  display: 'flex',
  flex: '0 0 50px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRight: '1px solid white',
  borderBottom: `1px solid ${palette.gray50}`,
  backgroundColor: palette.lightGray50,
  [mediaQueries.mobile]: {
    width: '30px',
    flex: '0 0 35px',
    display: props.mobileDisplay || 'none',
  },
}))

const DataColumnCheckbox = styled.div((props) => ({
  display: 'flex',
  flex: '0 0 50px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRight: '1px solid white',
  borderLeft: props.active ? `3px solid ${palette.orange}` : 'none',
  [mediaQueries.mobile]: {
    width: '30px',
    flex: '0 0 35px',
    display: props.mobileDisplay || 'none',
  },
}))

const TableCheckBox = ({ isBlocked, checked, onChange, checkStyle }) => {
  return (
    <label className="container">
      <input
        type="checkbox"
        checked={isBlocked ? false : checked}
        onChange={(e) => {
          if (isBlocked) return
          onChange(e.target.checked)
        }}
      />
      <span className="checkmark" style={checkStyle} />
    </label>
  )
}

const ActionButtonsLeft = styled.div({
  padding: '20px 13px',
  boxShadow: `0px 24px 66px -11px ${palette.lightGray50}`,
  [mediaQueries.mobile]: {
    flexWrap: 'wrap',
  },
})

const ActionButtonsCenter = styled.div((props) => ({
  display: props.noData ? 'none' : 'flex',
  justifyContent: 'center',
  width: '100%',
  margin: '20px 0 0 0',
  padding: '0 0 20px 0',
}))

const DataColumnValueNumber = styled.div({})

const NoDataDiv = styled.div(
  ({ height = 300, backgroundColor = palette.lightGray10 }) => ({
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    height,
    backgroundColor,
    color: palette.darkGray50,
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  }),
)

const NoDataIcon = styled.img(
  ({ margin, width = '64px', height = '58px' }) => ({
    width,
    height,
    margin: margin || '0 0 20px 0',
  }),
)

const Divider = styled.div((props) => ({
  width: '100%',
  height: '1px',
  background: props.color ? props.color : palette.lightGray70,
  margin: props?.margin || 0,
}))

const HeadingColumn = styled.div(() => ({
  display: 'flex',
  flexWrap: 'noWrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRight: '1px solid white',
  borderBottom: `1px solid ${palette.gray50}`,
  backgroundColor: palette.lightGray50,
  padding: '0 10px',
  minHeight: '50px',
  fontSize: '12px',
  fontFamily: fonts.primaryBold,
  textTransform: 'uppercase',
  ':last-child': {
    borderRight: 'none',
  },
  [mediaQueries.mobile]: {
    fontSize: '10px',
    padding: '5px',
  },
}))

const HeadingSocialColumn = styled.div((props) => {
  let bgColor = palette.lightGray50
  if (props.type === 'facebook') {
    bgColor = palette.facebook
  } else if (props.type === 'pinterest') {
    bgColor = palette.pinterest
  } else if (props.type === 'reddit') {
    bgColor = palette.reddit
  } else {
    bgColor = palette.orange
  }
  return {
    display: 'flex',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    flex: '0 0 100px',
    height: '50px',
    borderRight: '1px solid white',
    borderBottom: '1px solid white',
    padding: '0 16px',
    minHeight: '50px',
    fontSize: '12px',
    fontFamily: fonts.primaryBold,
    background: bgColor,
    ':last-child': {
      borderRight: 'none',
    },
    [mediaQueries.mobile]: {
      flex: '0 0 50px',
      padding: 0,
    },
  }
})

const DataColumn = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  wordBreak: 'break-word',
  position: 'relative',
  justifyContent: 'center',
  minHeight: '43px',
  padding: '10px',
  fontSize: '14px',
  cursor: 'pointer',
  borderRight: '1px solid white',
  ':last-child': {
    borderRight: 'none',
  },
}))

const DataColumnRow = styled.div((props) => ({
  padding: '20px 0',
  wordBreak: 'break-word',
  minWidth: '0px',
  maxWidth: '100%',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  ':nth-child(2)': {
    width: 'calc(100% - 45px)',
  },
  [mediaQueries.mobile]: {
    overflow: 'hidden',
    flex: 1,
  },
  ...(props.styles || {}),
}))

const DataColumnTitle = styled.div((props) => ({
  wordBreak: 'break-word',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  paddingBottom: '3px',
  color: props.color ? props.color : palette.darkGray,
  ...(props.styles || {}),
}))

const TypeLabel = styled.div((props) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: fonts.primaryMedium,
  fontSize: '10px',
  backgroundColor: props.active ? palette.white : palette.lightGray70,
  color: props.active ? palette.orange : palette.white,
  height: '21px',
  width: '49px',
  boxShadow: props.active ? '5px 0px 20px 0px rgba(19,23,35,0.12)' : 'none',
  marginRight: '10px',
}))

const SocialIcon = styled.img({
  height: '22px',
  width: '22px',
  [mediaQueries.mobile]: {
    height: '16px',
    width: '16px',
  },
})

const MobileDataColumn = styled.div({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  padding: '10px 13px',
  fontSize: '12px',
})
const MobileDataColumnRow = styled.div({
  display: 'flex',
  overflow: 'hidden',
  position: 'relative',
  fontSize: '12px',
})

const ValueWrapper = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  color: palette.darkGray,
}))

const Value = styled.div({
  fontFamily: fonts.primaryMedium,
  fontSize: '12px',
  marginLeft: '5px',
})

const MobileDataColumnValue = ({ icon, value }) => (
  <ValueWrapper>
    <SocialIcon src={icon} style={{ marginTop: '-2px' }} />
    <Value>{value}</Value>
  </ValueWrapper>
)

const NumberViewAll = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '0 10px 0 0',
  width: '30px',
})

const OuterWrapper = styled.div({
  boxShadow: `0px 10px 38px -16px ${palette.lightGray70}`,
  minWidth: '100%',
})

const TableActions = styled.div((props) => ({
  position: 'relative',
  minHeight: '50px',
  display: 'flex',
  paddingTop: '20px',
  paddingBottom: '10px',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  background: palette.white,
  '& > button': {
    flex: props.small ? '1 1 120px' : '1 1 207px',
    fontSize: '12px',
    margin: '0 10px 0 0',
  },
  [mediaQueries.mobile]: {
    flexWrap: 'wrap',
    margin: props.hasChildren ? 'inherit' : '0px',
    padding: props.hasChildren ? 'inherit' : '0px',
    minHeight: props.hasChildren ? 'inherit' : '0px',
  },
}))

const ActionLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginBottom: '16px',
  flexWrap: 'wrap',

  '& > div': {
    ':last-child': {
      marginTop: '8px',
    },
  },
})

const ActionRight = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginBottom: '16px',
})

const TableNotificationBar = styled.div({
  width: '100%',
  marginBottom: '24px',
})

const TableNotificationWrapper = styled.div(({ type }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '16px 50px 16px 16px',
  marginTop: '16px',
  width: '100%',
  fontSize: '14px',
  background:
    {
      success: setOpacity(palette.green, 0.4),
      warning: setOpacity(palette.warning, 0.4),
      info: setOpacity(palette.lightGray, 0.2),
      attention: setOpacity(palette.blue, 0.1),
    }[type] || setOpacity(palette.danger, 0.4),
  border:
    {
      success: `1px solid ${palette.green}`,
      warning: `1px solid ${palette.warning}`,
      info: `1px solid ${palette.lightGray}`,
      attention: `1px solid ${setOpacity(palette.blue, 0.3)}`,
    }[type] || `1px solid ${palette.danger}`,
  '& > div': {
    lineHeight: '24px',
  },
}))

const TableNotification = ({
  type,
  style,
  content,
  hideClose,
  closeBtnSize,
  onClose,
  closeBtnClass,
  testId,
}) => {
  return (
    <TableNotificationWrapper type={type} style={style}>
      {content}
      {!hideClose && (
        <CloseButton
          data-testid={testId}
          size={closeBtnSize}
          onClick={onClose}
          className={closeBtnClass || 'tw-right-4 tw-top-4'}
          closeBtnClass={closeBtnClass}
        />
      )}
    </TableNotificationWrapper>
  )
}

const TablePaginationWrapper = styled.div({
  fontSize: '18px',
  width: '100%',
  margin: '20px 0',
})

const TablePagination = ({ hidden, pagination, onPageChange }) => {
  if (hidden) return null

  return (
    <TablePaginationWrapper id="react-paginate">
      <ReactPaginate
        forcePage={pagination.page}
        previousLabel={<FaArrowLeft />}
        nextLabel={<FaArrowRight />}
        breakLabel="..."
        breakClassName="break-me"
        pageCount={pagination.pageCount}
        marginPagesDisplayed={1}
        pageRangeDisplayed={3}
        onPageChange={onPageChange}
        containerClassName="pagination"
        subContainerClassName="pages pagination"
        activeClassName="active"
      />
    </TablePaginationWrapper>
  )
}

const DataColumnWrapper = styled.div({
  display: 'flex',
})

const PageSizer = styled.div({
  display: 'flex',
})

const SubTablePageSizer = ({ sizes, onChange, active, ...props }) => {
  return (
    <PageSizer {...props}>
      <Text fontSize="16px" lineHeight="16px" margin="0 12px 0 0">
        {translate('results_per_page')}
      </Text>
      {sizes.map((size, index) => {
        return (
          <Text
            key={`pager-${index}`}
            fontSize="16px"
            lineHeight="16px"
            margin="0 8px 0 0"
            color={active === size ? palette.darkGray : palette.orange}
            onClick={() => onChange(size)}
            style={{ cursor: 'pointer' }}
          >
            {size}
          </Text>
        )
      })}
    </PageSizer>
  )
}

const ExportLoadingButton = ({ height }) => {
  return (
    <OutlineButton
      style={{
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none',
        height: height || '40px',
        marginRight: '8px',
        fontFamily: fonts.primaryMedium,
        fontSize: '14px',
      }}
    >
      {translate('export_to_csv_loading')}
      <span style={{ marginLeft: '8px' }}>
        <LoadingWheel width="16px" height="16px" />
      </span>
    </OutlineButton>
  )
}

const ButtonArea = styled.div({})

const RefreshArea = styled.div({
  display: 'flex',
  [mediaQueries.mobile]: {
    marginTop: '8px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& br': {
      display: 'none',
    },
  },
})

const SDUpdateCellWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
`

const SDUpdateCell = ({ updatedAt, onUpdate, isGlobalLocation = false }) => {
  const isMobile = useMediaQuery({ maxWidth: 767 })

  const render = () => {
    if (isGlobalLocation) {
      return null
    }

    return isUpdatedNow(updatedAt) ? (
      <Icon src={checkIcon} size="15px" margin="-5px 0 0 8px" />
    ) : (
      <ReactTooltip
        position="top-end"
        arrow
        distance={isMobile ? 30 : 0}
        html={
          <Text
            fontSize="12px"
            lineHeight="16px"
            color={palette.white}
            textAlign="left"
            style={{ width: 200 }}
            boldColor={palette.white}
            dangerouslySetInnerHTML={{
              __html: translate('update_sd_tooltip'),
            }}
          />
        }
      >
        <IconButton
          icon={refreshIcon}
          iconWidth="20px"
          iconHeight="20px"
          onClick={onUpdate}
        />
      </ReactTooltip>
    )
  }

  return <SDUpdateCellWrapper>{render()}</SDUpdateCellWrapper>
}

const SDValueCell = ({
  isGlobalLocation = false,
  value,
  updatedAt,
  useTWText,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 })

  return (
    <SDUpdateCellWrapper>
      <ReactTooltip
        arrow
        position="top"
        distance={isMobile ? 30 : 0}
        html={
          <Text
            fontSize="12px"
            lineHeight="16px"
            color={palette.white}
            textAlign="left"
            style={{ width: 200 }}
            boldColor={palette.white}
            dangerouslySetInnerHTML={{
              __html: translate(
                isGlobalLocation
                  ? 'seo_difficulty_disabled_message'
                  : 'updated_column_description',
              ),
            }}
          />
        }
      >
        <div className="tw-flex tw-items-baseline tw-gap-1 sm:tw-gap-3">
          <DataColumnValueNumber className="tw-w-fit tw-whitespace-nowrap">
            {useTWText ? (
              <TWText
                as="span"
                className="tw-text-sm tw-font-normal tw-leading-5 tw-text-zinc-950"
              >
                {formatNumber(value)}
              </TWText>
            ) : (
              formatNumber(value)
            )}
          </DataColumnValueNumber>
          {!isGlobalLocation && (
            <Text
              fontSize="10px"
              fontWeight={400}
              lineHeight="16px"
              textAlign="left"
              color={palette.darkGray}
              fontFamily="Roboto"
              className="tw-opacity-50"
            >
              ({getTimeLabel(updatedAt)})
            </Text>
          )}
        </div>
      </ReactTooltip>
    </SDUpdateCellWrapper>
  )
}

export {
  TableInnerWrapper,
  TableOuterWrapper,
  ScrollWrapper,
  DataRow,
  NoDataDiv,
  NoDataIcon,
  DataColumn,
  DataColumnBar,
  DataColumnValue,
  DataColumnValueNumber,
  DataGroup,
  DataColumnRow,
  DataColumnTitle,
  DataRowPrimary,
  OuterWrapper,
  TableTitle,
  HeadingRow,
  Label,
  SocialIcon,
  HeadingColumn,
  HeadingSocialColumn,
  HeadingColumnCheckbox,
  HeadingRegionalColumn,
  DataColumnCheckbox,
  ActionButtonsLeft,
  ActionButtonsCenter,
  Divider,
  TypeLabel,
  MobileDataColumn,
  MobileDataColumnRow,
  MobileDataColumnValue,
  NumberViewAll,
  TableActions,
  TableNotificationBar,
  TableNotification,
  TablePagination,
  ActionLeft,
  ActionRight,
  TableCheckBox,
  DataColumnWrapper,
  SubTablePageSizer,
  ExportLoadingButton,
  ButtonArea,
  RefreshArea,
  SDUpdateCell,
  SDValueCell,
}
